

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { EventContext } from "@/providers/EventProvider";
import { useGuests } from "@/providers/GuestsProvider";
import { useGuestDataUpdated } from "@/hooks/useGuestPatchSync";
import { mergeGuestPatch } from "@/lib/utils/guest-patch";
import type { GuestReconciledDetail } from "@/lib/guest-reconcile/reconcileScheduler";
import { useAiDataSync } from "@/lib/ai-sync/useAiDataSync";
import { motion } from "framer-motion";
import { Guest } from "@/types/Guest";
import { GuestDetailBody } from "./GuestDetailBody";
import { createPortal } from "react-dom";
import {
  getGuestsByIds,
  GUEST_REFETCH_FIELDS,
} from "@/lib/api/handlers/GuestHandling";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { hasDismissOwner } from "@/lib/a11y/dismissOwner";

type PGuestDetailsProps = {
  onCloseAction: () => void;
  guest: Guest;
  avoidStateUpdate?: boolean;
  notes?: boolean;
  // Overlay stacking. Defaults to the guestlist value; the standalone launcher
  // raises it (e.g. when opened over the slotting appointment modal at z-[80]).
  overlayZClass?: string;
};

// The refetch projection now lives beside GUEST_FIELDS in GuestHandling — one
// place, importable without an import cycle. Re-exported here for the callers
// that already imported it from this module.
export { GUEST_REFETCH_FIELDS };

export const PGuestDetails: React.FC<PGuestDetailsProps> = ({
  onCloseAction,
  guest,
  avoidStateUpdate = false,
  notes = true,
  overlayZClass = "z-50",
}) => {
  useSuppressCoach(); // hide bottom-right floaters over this modal
  // Local snapshot so a background SEND_EMAILS completion can refresh
  // the visible guest (state badge in particular) without forcing the
  // parent to re-mount the modal. Reset whenever the parent passes a
  // different guest (e.g. user navigates to a sibling).
  // Read ordering for one pane. Six triggers fire the same GET (open, focus,
  // check-in, send-completed, AI sync, a 20s poll) with no dedup, so responses
  // routinely overlap; whichever `.then` ran last used to win, which is arrival
  // order, not read order. Two counters settle it: only the NEWEST issued
  // request may write, and any request issued before a confirmed write is void
  // — that write already carries newer truth than the read can.
  const issuedSeq = useRef(0);
  const voidBefore = useRef(0);

  const [liveGuest, setLiveGuest] = useState<Guest>(guest);
  useEffect(() => {
    // A different guest replaces the pane's state. The SAME guest handed in as
    // a new object (StandaloneGuestDetail re-reads its row through react-query)
    // is treated like any other server read instead: it may not throw away what
    // the pane learned from its own GET. `emailUndeliverable` was the original
    // case (PB-TH-7): it arrived only through the pane's GET and would blink out
    // of an open bounce notice. The guest LIST projection carries it too since
    // it became a column, but a view-driven or restricted projection (companion
    // rows, registrations, an allowlisted role) still may not — the rule stays.
    setLiveGuest((prev) =>
      prev.guestId === guest.guestId ? { ...prev, ...guest } : guest,
    );
  }, [guest]);

  // `guest.eventId` is not populated on list-loaded guests — the guest list
  // projects the visible columns and eventId is usually not one of them — so
  // reading it directly made EVERY refetch below a no-op for exactly the guests
  // opened from the guest list, which is nearly all of them. Same fallback
  // chain GuestDetailBody already uses for its preview id.
  const { eventId: guestsEventId } = useGuests();
  const eventCtx = useContext(EventContext);
  const resolvedEventId =
    guest.eventId || guestsEventId || eventCtx?.event?.eventId;

  const refetchOpenGuest = useCallback(() => {
    if (!resolvedEventId || !guest.guestId) return;
    const seq = ++issuedSeq.current;
    getGuestsByIds(resolvedEventId, [guest.guestId], "", [], GUEST_REFETCH_FIELDS, 0, 1)
      .then((res) => {
        // A newer request is out or already home — this one only knows less.
        // This also covers the guest being SWAPPED under an open pane (a
        // sibling from the companion list, the requests table re-reading its
        // row): the component is not remounted for that, but the new guest id
        // re-runs the effect that fires this, so the previous guest's answer is
        // never the newest and cannot merge ITS fields into this one.
        if (seq !== issuedSeq.current) return;
        // A confirmed write landed after this request was sent, so its answer
        // predates that write. This is the reported bug: the 20s poll answering
        // just after a save put "no consent" back for another poll interval.
        if (seq <= voidBefore.current) return;
        if (!Array.isArray(res) || !res[0]) return;
        setLiveGuest((prev) => ({ ...prev, ...res[0] }));
      })
      .catch(() => {});
  }, [resolvedEventId, guest.guestId]);

  // The popup's own Save. `guest` is a SNAPSHOT the opener captured (the
  // guestlist hands its clicked row object, frozen for the popup's life), so a
  // confirmed save never reached `liveGuest` — the pane's read-only consumers
  // kept rendering pre-save values until the 20s poll caught up. Most visibly
  // the send-to-guest dialog, which derives its consent warning from
  // `guest.emailOptIn`: flip Mail-Opt-in to "Ja", save, open "E-Mails senden"
  // → still "keine hinterlegte Einwilligung".
  const applySavePatch = useCallback((patch: Partial<Guest>) => {
    voidBefore.current = issuedSeq.current;
    setLiveGuest((prev) => mergeGuestPatch(prev, patch));
  }, []);
  useGuestDataUpdated(guest.guestId, resolvedEventId, applySavePatch);

  // Pull once on open. The guest handed in comes from a LIST whose projection
  // may be narrower than the pane (registrations, companions, an allowlisted
  // role), so a field that list does not request arrives undefined here. The
  // original case was emailUndeliverable, which the pane needs to warn that
  // nothing is being delivered to this guest (PB-TH-7); the guest list requests
  // it itself now that it is a column, the other openers still do not. Without
  // this the warning appeared only after the 20s poll or a window focus, i.e.
  // usually after the organiser had already closed the pane. One row.
  useEffect(() => {
    refetchOpenGuest();
  }, [refetchOpenGuest]);

  useEffect(() => {
    if (!resolvedEventId || !guest.guestId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.eventId !== resolvedEventId) return;
      refetchOpenGuest();
    };
    window.addEventListener("sendEmailsCompleted", handler);
    // Same for a manual SMS send (state CONTACTED, F-3).
    window.addEventListener("sendSmsCompleted", handler);

    // A workflow UPDATE_GUEST runs ASYNC on the consumer, so the patch the save
    // broadcast can already be outdated when the reconcile lands. GuestActions
    // applies that fresh guest to the FORM only — without this the pane's
    // read-only consumers (send dialog, QR, badge link) would assert the
    // client's value while the server holds another, until the poll. Server
    // truth, so it also moves the epoch.
    const onReconciled = (e: Event) => {
      const detail = (e as CustomEvent<GuestReconciledDetail>).detail;
      if (!detail?.guest || detail.guestId !== guest.guestId) return;
      if (detail.eventId !== resolvedEventId) return;
      voidBefore.current = issuedSeq.current;
      // Merged, not `applyServerTruth`: that one REPLACES the row, and the
      // reconcile projection is narrower than what this pane holds (loginToken,
      // appointments, seatInfo, contactId…), so a replace blanks whatever the
      // open layout happens to show outside it. The fresh values still win —
      // including `state`/`acceptanceState`, which is the entire reason to
      // listen — they just do not take the rest of the guest with them.
      setLiveGuest((prev) => ({ ...prev, ...detail.guest }));
    };
    window.addEventListener("guestReconciled", onReconciled);

    // The header's check-in button already fires this (as do the table CTA and
    // the bulk job) but only its own local state reflected the result — so a
    // display-only `checkedIn` / `checkedInAt` cell in the layout sat inches
    // below a button that showed the opposite. Re-pull instead of duplicating
    // the state: the backend also stamps `checkedInAt`, which the optimistic
    // toggle cannot know. No detail filter — the event carries none, and the
    // fetch is one row.
    window.addEventListener("checkInActionCompleted", refetchOpenGuest);

    // Workflow actions (e.g. UPDATE_GUEST on guest created/updated) mutate the
    // guest server-side in the queue consumer with no client event. Re-pull on
    // window focus and on a light interval while the pane is open so those
    // changes appear without a manual reload. The single-guest fetch is cheap
    // (1 row) and the merge is lossless.
    window.addEventListener("focus", refetchOpenGuest);
    // Pause polling while the tab is hidden; the focus listener refetches on
    // return, so a backgrounded pane needn't poll.
    const poll = window.setInterval(() => {
      if (!document.hidden) refetchOpenGuest();
    }, 20000);

    return () => {
      window.removeEventListener("sendEmailsCompleted", handler);
      window.removeEventListener("sendSmsCompleted", handler);
      window.removeEventListener("guestReconciled", onReconciled);
      window.removeEventListener("checkInActionCompleted", refetchOpenGuest);
      window.removeEventListener("focus", refetchOpenGuest);
      window.clearInterval(poll);
    };
  }, [resolvedEventId, guest.guestId, refetchOpenGuest]);

  // AI ("Velo") server tools can edit this guest directly — pull fresh fields in
  // so the open detail pane reflects it without a reload.
  useAiDataSync("guests", resolvedEventId, refetchOpenGuest);

  const content = (
    <motion.div
      className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${overlayZClass}`}
      onMouseDown={(e) => {
        // A press that closes an open picker list inside the popup (a select
        // custom field in inline edit) must not also close the popup — the
        // unsaved edits in it go with it, and there is no discard prompt.
        if (e.target === e.currentTarget && !hasDismissOwner(e.currentTarget as Element)) onCloseAction();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full sm:w-auto"
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <GuestDetailBody
          notes={notes}
          onCloseAction={onCloseAction}
          guest={liveGuest}
          avoidStateUpdate={avoidStateUpdate}
        />
      </motion.div>
    </motion.div>
  );

  return typeof window !== "undefined"
    ? createPortal(content, document.body)
    : null;
};
